import angular from 'angular';
import { configInstance } from '@core/configuracao/nsj-config';
import { LoadParams } from '../../../../shared/models/loadparams.model';
import { IAjustePonto, AjustePontoSituacao } from './models/ajusteponto.model';

export class AjustesPontoService {
    static $inject = ['$http', 'nsjRouting', '$rootScope', '$q'];

    public entities: IAjustePonto[] = [];
    public loaded = false;
    public constructors: any = {};
    public after: any = {};
    public filters: any = {};
    public loadParams: LoadParams;
    public loading_deferred: any = null;
    public filter: string = '';

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: any,
        public $rootScope: angular.IRootScopeService,
        public $q: angular.IQService,
    ) {
        this.loadParams = new LoadParams();
    }

    reload() {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this.loadParams.finished = false;
        this.loadParams.to_load = 1;
        this.after = {};
        this.loaded = false;
        this.entities.length = 0;
        this.loadParams.busy = false;

        return this.load();
    }

    _load(constructors: any, offset: any, filter: any) {
        this.loading_deferred = this.$q.defer();

        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'GET',
                url: this.nsjRouting.generate('gestaoponto_get', angular.extend({}, constructors, { 'offset': offset, 'filter': filter }, this.filters), true),
                timeout: this.loading_deferred.promise
            })
            .then((response: any) => {
                resolve(response.data);
            })
            .catch((response: any) => {
                reject(response);
            });
        });
    }

    load() {
        if (!this.loadParams.busy && !this.loadParams.finished && this.loadParams.to_load > 0) {
            this.loadParams.busy = true;

            this.$rootScope.$broadcast('ajustesponto_list_loading');

            this._load(this.constructors, this.after, this.filter)
                .then((data: any): void => {
                    if (data.length > 0) {
                        for (var i = 0; i < data.length; i++) {
                            this.entities.push(data[i]);
                        }
                    }

                    this.loadParams.finished = true;
                    this.loaded = true;
                    this.loadParams.to_load--;

                    this.$rootScope.$broadcast('ajustesponto_list_finished', this.entities);
                })
                .catch((error: any): void => {
                    if (error.xhrStatus !== 'abort') {
                        this.loadParams.finished = true;
                    }

                    this.$rootScope.$broadcast('ajustesponto_list_error', error);
                })
                .finally((): void => {
                    if (this.loaded || this.loadParams.finished) {
                        this.loadParams.busy = false;
                    }

                    this.load();
                });
        }

        return this.entities;
    }

    loadMore() {
        this.loadParams.to_load = 3;
        this.load();
    }

    // --- exportação para excel ---
    public exportar(constructors: any): angular.IPromise<Blob> {
        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'GET',
                url: this.nsjRouting.generate('gestaoponto_exportacao', angular.extend({}, constructors), true),
                responseType: 'blob'
            })
            .then((response: angular.IHttpResponse<any>) => {
                resolve(response.data);
            })
            .catch((response: any) => {
                reject(response);
            });
        });
    }

    // --- aprovar ajuste ---
    public aprovarAjuste(ajustePontoId: string): angular.IPromise<any> {
        if (!ajustePontoId) {
            return this.$q.reject('ID do ajuste de ponto é obrigatório');
        }

        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'POST',
                url: this.nsjRouting.generate('gestaoponto_aprovar', { id: ajustePontoId }, true),
            })
            .then((response: angular.IHttpResponse<any>) => {
                const data = response?.data;
                if (data?.sucesso) {
                    const ajuste = this.entities.find((a) => a.gestaoponto === ajustePontoId);
                    if (ajuste) {
                        ajuste.status = AjustePontoSituacao.Aprovado;
                    }
                    this.$rootScope.$broadcast('meurh_ajustesponto_aprovar_loaded', { data });
                }
                resolve(data);
            })
            .catch((error: any) => {
                reject(error);
            });
        });
    }

    // --- recusar ajuste ---
    public recusarAjuste(ajustePontoId: string, motivo: string): angular.IPromise<any> {
        if (!ajustePontoId) {
            return this.$q.reject('ID do ajuste de ponto é obrigatório');
        }

        if (!motivo || !motivo.trim()) {
            return this.$q.reject('O motivo da recusa é obrigatório');
        }

        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'POST',
                url: this.nsjRouting.generate('gestaoponto_recusar', { id: ajustePontoId }, true),
                data: { justificativa: motivo.trim() }
            })
            .then((response: angular.IHttpResponse<any>) => {
                const data = response?.data;
                if (data?.sucesso) {
                    const ajuste = this.entities.find((a) => a.gestaoponto === ajustePontoId);
                    if (ajuste) {
                        ajuste.status = AjustePontoSituacao.Recusado;
                        ajuste.motivo = motivo.trim();
                    }
                    this.$rootScope.$broadcast('meurh_ajustesponto_recusar_loaded', { data });
                }
                resolve(data);
            })
            .catch((error: any) => {
                reject(error);
            });
        });
    }

    // --- aprovar em lote ---
    public aprovarAjustesLote(ajustesPontoIds: string[]): angular.IPromise<any> {
        if (!Array.isArray(ajustesPontoIds) || !ajustesPontoIds.length) {
            return this.$q.reject('É necessário informar pelo menos um ajuste para aprovar.');
        }

        const payload = {
            itens: ajustesPontoIds.map((id) => ({ id }))
        };

        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'POST',
                url: this.nsjRouting.generate('gestaoponto_aprovar_lote', {}, true),
                data: payload
            })
            .then((response: angular.IHttpResponse<any>) => {
                const data = response?.data;
                const resultados = Array.isArray(data?.resultado) ? data.resultado : [];
                const aprovados = resultados.filter((item: any) => item?.sucesso).map((item: any) => item.id);
                const idSet = new Set(aprovados);

                if (idSet.size) {
                    this.entities.forEach((ajuste) => {
                        if (idSet.has(ajuste.gestaoponto)) {
                            ajuste.status = AjustePontoSituacao.Aprovado;
                        }
                    });
                }

                this.$rootScope.$broadcast('meurh_ajustesponto_aprovar_lote_loaded', { data });
                resolve(data);
            })
            .catch((error: any) => {
                reject(error);
            });
        });
    }

    // --- recusar em lote ---
    public recusarAjustesLote(ajustesPontoIds: string[], motivo: string): angular.IPromise<any> {
        if (!Array.isArray(ajustesPontoIds) || !ajustesPontoIds.length) {
            return this.$q.reject('É necessário informar pelo menos um ajuste para recusar.');
        }

        if (!motivo || !motivo.trim()) {
            return this.$q.reject('O motivo da recusa é obrigatório');
        }

        const justificativa = motivo.trim();
        const payload = {
            itens: ajustesPontoIds.map((id) => ({ id, justificativa }))
        };

        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'POST',
                url: this.nsjRouting.generate('gestaoponto_recusar_lote', {}, true),
                data: payload
            })
            .then((response: angular.IHttpResponse<any>) => {
                const data = response?.data;
                const resultados = Array.isArray(data?.resultado) ? data.resultado : [];
                const recusados = resultados.filter((item: any) => item?.sucesso).map((item: any) => item.id);
                const idSet = new Set(recusados);

                if (idSet.size) {
                    this.entities.forEach((ajuste) => {
                        if (idSet.has(ajuste.gestaoponto)) {
                            ajuste.status = AjustePontoSituacao.Recusado;
                            ajuste.motivo = justificativa;
                        }
                    });
                }

                this.$rootScope.$broadcast('meurh_ajustesponto_recusar_lote_loaded', { data });
                resolve(data);
            })
            .catch((error: any) => {
                reject(error);
            });
        });
    }

    public getTrabalhadorFoto(identifier: string): angular.IPromise<string> {
        if (!identifier) {
            return this.$q.reject('ID do trabalhador é obrigatório');
        }

        return this.$q((resolve: any, reject: any) => {
            this.$http
                .get(this.nsjRouting.generate('persona_trabalhadores_foto', { id: identifier }, true), { responseType: 'arraybuffer' })
                .then((response: any) => {
                    const mimeType = response.headers('Content-Type') || response.headers('content-type');
                    const file = new Blob([response.data], { type: mimeType });
                    const fileUrl = URL.createObjectURL(file);
                    resolve(fileUrl);
                })
                .catch((response: any) => {
                    reject(response);
                });
        });
    }

    // --- anexos ---
    public getAnexoUrl(ajuste: IAjustePonto): string {
        const globals = configInstance.config?.globals;
        const params = {
            tenant: globals?.tenantCodigo,
            grupoempresarial: ajuste.grupoempresarial?.grupoempresarial || globals?.grupoEmpresarialCodigo,
            trabalhador: ajuste.trabalhador?.trabalhador,
            origem: ajuste.origem,
            id: ajuste.detalhes.anexo_id
        };
        return this.nsjRouting.generate('meurh_gestaopontoanexos_anexo', params, true);
    }

    public getAnexoBlob(url: string): angular.IPromise<any> {
        return this.$http.get(url, { responseType: 'arraybuffer' })
            .then(response => {
                let contentType = response.headers('Content-Type') || response.headers('content-type');

                if (typeof response.data === 'string') {
                    const parsed = this.parseBase64Payload(response.data, contentType);
                    if (parsed.base64) {
                        const byteCharacters = atob(parsed.base64);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        contentType = parsed.contentType || contentType;
                        return {
                            data: byteArray.buffer,
                            contentType: contentType
                        };
                    }
                }

                if (!contentType || contentType === 'application/octet-stream') {
                    contentType = this.detectContentType(response.data as any) || contentType;
                }

                return {
                    data: response.data,
                    contentType: contentType
                };
            });
    }

    private parseBase64Payload(payload: string, fallbackContentType?: string): { base64: string | null; contentType?: string } {
        const trimmed = payload.trim();
        if (trimmed.startsWith('data:')) {
            const parts = trimmed.split(';base64,');
            if (parts.length === 2) {
                return {
                    base64: parts[1],
                    contentType: parts[0].replace('data:', '') || fallbackContentType
                };
            }
        }

        const normalized = trimmed.replace(/\s+/g, '');
        if (/^[A-Za-z0-9+/=]+$/.test(normalized) && normalized.length > 0) {
            return { base64: normalized, contentType: fallbackContentType };
        }

        return { base64: null, contentType: fallbackContentType };
    }

    private detectContentType(buffer: ArrayBuffer | Uint8Array): string | null {
        const view = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        if (view.length >= 4) {
            // pdf: 25 50 44 46 => %PDF
            if (view[0] === 0x25 && view[1] === 0x50 && view[2] === 0x44 && view[3] === 0x46) {
                return 'application/pdf';
            }
            // png: 89 50 4E 47
            if (view[0] === 0x89 && view[1] === 0x50 && view[2] === 0x4e && view[3] === 0x47) {
                return 'image/png';
            }
            // jpg: FF D8 FF
            if (view[0] === 0xff && view[1] === 0xd8 && view[2] === 0xff) {
                return 'image/jpeg';
            }
        }

        return null;
    }
}
